import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CssBaseline, ThemeProvider, createTheme, Typography, CircularProgress } from '@mui/material';
import AnonymousChatInterface from '../components/AnonymousChat/AnonymousChatInterface';
import { storeOrganization } from '../utils/organizationSession';
import { apiService } from '../services/api';
import { useAnonymousAuth } from '../hooks/useAnonymousAuth';

// Use the same theme as the main app for consistency
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          minWidth: '320px',
          margin: 0,
          padding: 0,
        },
        html: {
          margin: 0,
          padding: 0,
        },
      },
    },
  },
});

// Small helper component that preloads organization context (by token) before ensuring session
const AnonAuthBootstrap: React.FC<{ orgToken?: string; children: React.ReactNode }> = ({ orgToken, children }) => {
  const { ensureSession, loading, error } = useAnonymousAuth();
  const [orgReady, setOrgReady] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (orgToken) {
          // Resolve token -> organization and persist for session creation
          const org = await apiService.organizations.getByToken(orgToken);
          if (!active) return;
          storeOrganization(org);
        }
      } catch (e) {
        console.warn('Failed to resolve organization by token:', e);
      } finally {
        if (active) setOrgReady(true);
      }
    })();
    return () => { active = false; };
  }, [orgToken]);

  useEffect(() => {
    if (orgReady) {
      // Now that organization is set (or not required if provided via ?org), ensure the anon session
      ensureSession();
    }
  }, [orgReady, ensureSession]);

  // Keep UI minimal; AnonymousChatInterface shows its own content
  return (
    <>
      {/* Optionally, show lightweight boot status at top-level */}
      {loading && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption">Starting session…</Typography>
        </Box>
      )}
      {children}
      {error && (
        <Box sx={{ position: 'absolute', bottom: 12, left: 12, right: 12, color: '#b00020' }}>
          <Typography variant="caption">{error}</Typography>
        </Box>
      )}
    </>
  );
};

const AnonymousChatPage: React.FC = () => {
  const { orgToken } = useParams<{ orgToken?: string }>();

  // Lazy import to avoid tree-shaking issues if context is unused elsewhere
  const { AnonymousAuthProvider } = require('../contexts/AnonymousAuthContext');

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Disable autoEnsure so we can preload organization by token before creating session */}
      <AnonymousAuthProvider autoEnsure={false}>
        <AnonAuthBootstrap orgToken={orgToken}>
          <Box
            sx={{
              width: '100vw',
              height: '100vh',
              overflow: 'hidden',
              margin: 0,
              padding: 0,
            }}
          >
            <AnonymousChatInterface orgToken={orgToken} />
          </Box>
        </AnonAuthBootstrap>
      </AnonymousAuthProvider>
    </ThemeProvider>
  );
};

export default AnonymousChatPage;
